/**
 * This file is maintained by Stencil, do not directly modify this file
 */
'use client';

import React, {useEffect, useRef} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {initializeVendors} from '@/lib/vendors';
import {useI18n} from '@udemy-v2/i18n';
import {GoogleTagManager} from '@udemy-v2/gtag';

/**
 * Props for the Vendors component
 */
export interface VendorsProps {
    children?: React.ReactNode;
}

/**
 * Vendors component that initializes third-party services and renders vendor components
 * Uses common data from context to set up vendors like Sentry
 * Renders vendor components like GoogleTagManager
 *
 * @component
 * @param {Readonly<VendorsProps>} props - Component props with readonly constraint
 * @returns {JSX.Element} Vendor components and children wrapped with vendor initialization
 */
export function Vendors({children}: Readonly<VendorsProps>) {
    // Get common data from the CommonProviders context
    const commonData = useCommonData();
    const {locale} = useI18n();

    // Track if vendors have been initialized to prevent multiple calls
    const vendorsInitialized = useRef(false);

    useEffect(() => {
        // Initialize vendors only once when common data is available and fully loaded
        if (commonData.page?.isLoading === false && !vendorsInitialized.current) {
            try {
                initializeVendors(commonData, locale);
                vendorsInitialized.current = true;
            } catch (error) {
                // Log error but don't break the component
                console.error('Failed to initialize vendors:', error);
            }
        }
    }, [commonData, locale]);

    // Render vendor components and children
    return (
        <>
            <GoogleTagManager />
            {children}
        </>
    );
}
